import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import { SectionLabel, Reveal } from '../components/Section';
import TournamentCard from '../components/TournamentCard';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'ongoing', label: 'Ongoing' },
  { key: 'completed', label: 'Completed' },
];

const GAME_LABELS = {
  bgmi: 'BGMI',
  freefire: 'Free Fire',
  free_fire: 'Free Fire',
  ff: 'Free Fire',
  valorant: 'Valorant',
  codm: 'COD Mobile',
};

export function gameLabel(g) {
  const key = (g || '').toLowerCase().trim();
  return GAME_LABELS[key] || key.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const OFFICIAL_HOSTS = ['kroy esports', 'super admin', 'match admin', 'kroy', 'admin', 'host admin'];
const OFFICIAL_LABEL = 'KROY Official';
const isOfficialHost = (h) => OFFICIAL_HOSTS.includes((h || '').toLowerCase().trim());
// Public org name (host_name, set by the app for external hosts like "Shyam Gaming")
// wins over the internal hosted_by admin label — any new org auto-gets its own showcase.
const hostOf = (t) => (t.host_name || '').trim() || (t.hosted_by || '').trim();

const MONOGRAM_COLORS = ['#e8173a', '#00c853', '#f5c518', '#3b82f6', '#f97316', '#8b5cf6'];
const monogramColor = (name) => {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return MONOGRAM_COLORS[h % MONOGRAM_COLORS.length];
};
const initials = (name) => {
  const w = name.trim().split(/\s+/).filter(Boolean);
  const s = ((w[0] && w[0][0]) || '') + (w.length > 1 && w[1] ? w[1][0] : '');
  return s.toUpperCase() || '?';
};

function HostHeader({ group, stats }) {
  const slug = group.key.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const color = monogramColor(group.host);
  return (
    <div className="mb-5 flex items-center gap-4" data-testid={`host-header-${slug}`}>
      {group.host === OFFICIAL_LABEL ? (
        <img
          src="/kroy-emblem.png"
          alt="KROY Esports"
          className="w-11 h-11 object-contain shrink-0"
          data-testid={`host-logo-${slug}`}
        />
      ) : stats && stats.logo_url ? (
        <img
          src={stats.logo_url}
          alt={group.host}
          className="w-11 h-11 rounded-xl object-cover border border-white/10 shrink-0"
          data-testid={`host-logo-${slug}`}
        />
      ) : (
        <span
          className="w-11 h-11 rounded-xl flex items-center justify-center font-display text-sm shrink-0"
          style={{ background: `${color}22`, border: `1px solid ${color}55`, color }}
          data-testid={`host-logo-${slug}`}
        >
          {initials(group.host)}
        </span>
      )}
      <div className="min-w-0">
        <h2 className="text-xs font-extrabold uppercase tracking-[0.25em] text-[#8a8a8a] flex items-center gap-3">
          <span className="w-6 h-px bg-[#e8173a] shrink-0" />
          <span className="truncate">{group.label}</span>
        </h2>
        {stats && (
          <p className="mt-1.5 text-[11px] font-semibold text-[#6a6a6a]" data-testid={`host-stats-${slug}`}>
            {stats.tournaments} tournament{stats.tournaments === 1 ? '' : 's'} hosted
            {stats.prize_pool > 0 && <> &bull; &#8377;{stats.prize_pool.toLocaleString('en-IN')} prize pool</>}
            {stats.players > 0 && <> &bull; {stats.players.toLocaleString('en-IN')} players</>}
          </p>
        )}
      </div>
    </div>
  );
}

export default function Tournaments() {
  const [tab, setTab] = useState('all');
  const [game, setGame] = useState('all');
  const [games, setGames] = useState([]);
  const [list, setList] = useState(null);
  const [hosts, setHosts] = useState({});

  useEffect(() => {
    setList(null);
    let alive = true;
    const load = () =>
      api
        .tournaments(tab)
        .then((d) => {
          if (!alive) return;
          const ts = d.tournaments || [];
          setList(ts);
          setGames((prev) => Array.from(new Set([...prev, ...ts.map((t) => t.game).filter(Boolean)])));
        })
        .catch(() => {
          if (alive) setList([]);
        });
    const loadHosts = () =>
      api
        .hosts()
        .then((d) => {
          if (!alive) return;
          const map = {};
          (d.hosts || []).forEach((h) => {
            map[h.name] = h;
          });
          setHosts(map);
        })
        .catch(() => {});
    load();
    loadHosts();
    const i = setInterval(() => {
      load();
      loadHosts();
    }, 60000);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, [tab]);

  const visible =
    list === null ? null : game === 'all' ? list : list.filter((t) => (t.game || '').toLowerCase() === game.toLowerCase());

  const groups = [];
  if (visible !== null) {
    const official = visible.filter((t) => !hostOf(t) || isOfficialHost(hostOf(t)));
    const byHost = {};
    visible
      .filter((t) => hostOf(t) && !isOfficialHost(hostOf(t)))
      .forEach((t) => {
        (byHost[hostOf(t)] = byHost[hostOf(t)] || []).push(t);
      });
    if (official.length) groups.push({ key: 'kroy-official', label: OFFICIAL_LABEL, host: OFFICIAL_LABEL, list: official });
    Object.entries(byHost).forEach(([host, l]) => groups.push({ key: host, label: `Hosted by ${host}`, host, list: l }));
  }

  return (
    <main className="min-h-screen" data-testid="tournaments-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <SectionLabel testid="tournaments-label">The Arena</SectionLabel>
        <Reveal>
          <h1 className="mt-5 font-display text-white text-4xl sm:text-6xl">
            Tournaments<span className="text-[#e8173a]">.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-4 text-[#9e9e9e] text-sm sm:text-base max-w-lg">
            Every BGMI and Free Fire battle, live from the KROY arena. Registration happens inside the app.
          </p>
        </Reveal>

        <div className="mt-10 flex gap-2 flex-wrap" data-testid="tournament-filter-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tournament-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`relative px-5 py-2.5 rounded-full text-xs font-extrabold uppercase tracking-wider transition-colors duration-300 ${
                tab === t.key ? 'text-white' : 'text-[#8a8a8a] hover:text-white border border-white/10'
              }`}
            >
              {tab === t.key && (
                <motion.span
                  layoutId="tournament-tab-pill"
                  className="absolute inset-0 bg-[#e8173a] rounded-full"
                  transition={{ type: 'spring', bounce: 0.25, duration: 0.55 }}
                />
              )}
              <span className="relative">{t.label}</span>
            </button>
          ))}
        </div>

        {games.length > 0 && (
          <div className="mt-4 flex gap-2 flex-wrap" data-testid="game-filter-tabs">
            {[{ key: 'all', label: 'All Games' }, ...games.map((g) => ({ key: g, label: gameLabel(g) }))].map((g) => (
              <button
                key={g.key}
                data-testid={`game-tab-${g.key.toLowerCase().replace(/[\s_]+/g, '-')}`}
                onClick={() => setGame(g.key)}
                className={`relative px-4 py-2 rounded-full text-[10px] font-extrabold uppercase tracking-widest transition-colors duration-300 ${
                  game === g.key ? 'text-white' : 'text-[#8a8a8a] hover:text-white border border-white/10'
                }`}
              >
                {game === g.key && (
                  <motion.span
                    layoutId="game-tab-pill"
                    className="absolute inset-0 bg-[#e8173a] rounded-full"
                    transition={{ type: 'spring', bounce: 0.25, duration: 0.5 }}
                  />
                )}
                <span className="relative">{g.label}</span>
              </button>
            ))}
          </div>
        )}

        {visible === null ? (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-48 rounded-xl bg-[#1a1f2e] animate-pulse" data-testid={`tournament-skeleton-${i}`} />
            ))}
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.key} className="mt-10" data-testid={`host-group-${g.key.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <HostHeader group={g} stats={hosts[g.host]} />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {g.list.map((t, i) => (
                  <TournamentCard key={t.id} t={t} index={i} />
                ))}
              </div>
            </div>
          ))
        )}
        {visible !== null && visible.length === 0 && (
          <div className="mt-10 card-surface p-10 text-center" data-testid="tournaments-empty">
            <p className="text-white font-bold">
              No {game !== 'all' ? gameLabel(game) : ''} {tab === 'all' ? '' : tab} tournaments right now
            </p>
            <p className="mt-2 text-sm text-[#8a8a8a]">New battles drop daily — check back soon or download the app for instant alerts.</p>
          </div>
        )}
      </div>
    </main>
  );
}
